import React, { memo, ReactElement } from 'react'

import { clsx } from 'clsx'

import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'
import { isJerseyProduct } from '@dfds-pax/utils'

import Price from '@/components/shared/Price'

import { useTranslation } from '@/hooks/useDebugTranslations'

interface Props {
  components?: BffInterfacesDeparture.DeparturePricesComponent[]
  currency: string
  departureId: string
  hidePriceAdjustment: boolean
  offerPrice?: number
  onTierSelection: () => void
  price: number
  productCode: string
  selectedType: boolean
  soldOut: boolean
}

const TierSelector: React.FC<Props> = ({
  components,
  currency,
  departureId,
  hidePriceAdjustment,
  offerPrice,
  onTierSelection,
  price,
  productCode,
  selectedType,
  soldOut,
}): ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <div
        className={clsx(
          'p-semantic-global-md md:p-semantic-global-lg flex h-fit w-full items-center',
          (selectedType || soldOut) && 'cursor-default',
          !selectedType && !soldOut && 'cursor-pointer'
        )}
        onClick={() => !selectedType && !soldOut && onTierSelection()}
      >
        <div className="flex w-full flex-row">
          <div className="flex flex-row items-center">
            {!soldOut && !isJerseyProduct(productCode) ? (
              <div className="mr-primitive-8 flex h-full w-[20px] items-center justify-center">
                <div
                  className={clsx(
                    `border-semantic-global-background-white outline-semantic-global-primary-default h-[14px] w-[14px] rounded-full border-3 outline-1`,
                    selectedType ? 'bg-semantic-global-primary-default pointer-events-none' : 'bg-semantic-semantic-global-background-white'
                  )}
                  data-e2e-checked={selectedType}
                />
              </div>
            ) : null}

            <div
              className={clsx(
                'typography-heading-md',
                soldOut ? 'text-component-radio-group-fill-color-disabled-selected' : 'text-component-radio-group-none'
              )}
            >
              {t(`${productCode}-HEADLINE`)}
            </div>
          </div>

          <div className="ml-auto">
            <div
              data-e2e="ticketPrice"
              data-e2e-price={offerPrice ?? price}
              data-e2e-selected={selectedType}
              data-e2e-tickettype={productCode}
              onClick={() => {
                if (window.location.origin !== 'https://www.dfds.com')
                  console.log(`[${departureId}] ${productCode} components linked:`, components)
              }}
            >
              <div>
                {!soldOut ? (
                  <div className="flex flex-col items-end">
                    {offerPrice !== undefined && !hidePriceAdjustment ? (
                      <div className="gap-semantic-global-2xs flex h-full flex-col items-center justify-end">
                        <span className="typography-number-md md:typography-heading-md bg-semantic-global-success-dark text-semantic-global-background-white p-semantic-global-2xs">
                          <Price currency={currency} dataAttr="departure-tier-offer-price" price={offerPrice} />
                        </span>
                        <span className="typography-body-md-regular text-semantic-global-text-light mr-primitive-4 w-full text-right line-through">
                          <Price currency={currency} dataAttr="departure-tier-line-through-price" price={price} />
                        </span>
                      </div>
                    ) : null}
                    {offerPrice !== undefined && hidePriceAdjustment ? (
                      <span className="typography-number-md md:typography-heading-md text-semantic-global-text-default">
                        <Price currency={currency} dataAttr="departure-tier-offer-price" price={offerPrice} />
                      </span>
                    ) : null}
                    {!offerPrice ? (
                      <span className="typography-number-md md:typography-heading-md text-semantic-global-text-default">
                        <Price currency={currency} dataAttr="departure-tier-price" price={price} />
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className={clsx('typography-number-md md:typography-heading-md text-semantic-global-disabled-default uppercase')}>
                    {t('GENERAL-SOLD-OUT')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-semantic-global-border-light border-t" />
    </>
  )
}

export default memo(TierSelector)
